import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './ChatClient.module.scss';
import TextField from '@atlaskit/textfield';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import { LoadingButton } from '@atlaskit/button';
import { BoardContext } from '../../context/BoardContext';

import moment from 'moment';

import Avatar from '@atlaskit/avatar';
import { TrelloPowerUpContext } from '@powerupsclub/trello';
import { PaymentsContext } from '@powerupsclub/payments';

let openedPaymentsAt: Date | undefined;

export const ChatClient: React.FC = () => {
  const { isAuthorized, authorize, isAuthorizing, trelloIframe } =
    useContext(TrelloPowerUpContext);
  const { trialEndDate, subscription, refetchSubscription } =
    useContext(PaymentsContext);

  const {
    isSending,
    sendMessage,
    messages,
    lastMessageViewedId,
    me,
    members,
    deleteMessage,
  } = useContext(BoardContext);

  const [incomingMessage, setIncomingMessage] = useState('');

  const chatMessagesRef = useRef<HTMLDivElement | null>(null);

  const onSubmit = useCallback(async () => {
    const trimmedMessage = incomingMessage.trim();
    if (trimmedMessage.length) {
      sendMessage(trimmedMessage);
      setIncomingMessage('');
    }
  }, [incomingMessage, sendMessage]);

  const onPayments = useCallback(async () => {
    openedPaymentsAt = new Date();

    trelloIframe.modal({
      url: './?payment=true',
      fullscreen: true,
    });
  }, [trelloIframe]);

  // const scrollToBottom = () => {
  //   if (chatMessagesRef.current && chatMessagesRef.current.scrollHeight) {
  //     chatMessagesRef.current.scrollTo({
  //       top: chatMessagesRef.current.scrollHeight,
  //       behavior: 'smooth',
  //     });
  //   }
  // };

  const onTRender = useCallback(async () => {
    if (openedPaymentsAt) {
      const lastSubscriptionReRender = await trelloIframe.get(
        'board',
        'private',
        'rerender:subscription'
      );

      if (lastSubscriptionReRender) {
        if (new Date(lastSubscriptionReRender) > openedPaymentsAt) {
          refetchSubscription();

          openedPaymentsAt = undefined;
        }
      }
    }
  }, [refetchSubscription, trelloIframe]);

  useEffect(() => {
    trelloIframe.render(async () => {
      onTRender();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onScroll = useCallback(() => {
    if (chatMessagesRef.current?.scrollTop === 0) {
      if (messages[0]) {
        if (!lastMessageViewedId || lastMessageViewedId !== messages[0].id) {
          trelloIframe.set(
            'board',
            'private',
            'lastMessageViewedId',
            `${me?.id}:${messages[0].timetoken}`
          );
        }
      }
    }
  }, [lastMessageViewedId, me?.id, messages, trelloIframe]);

  useEffect(() => {
    const ref = chatMessagesRef.current;
    if (!ref) {
      return;
    }

    ref.addEventListener('scroll', onScroll);

    return () => {
      if (!ref) {
        return;
      }

      ref.removeEventListener('scroll', onScroll);
    };
  }, [onScroll]);

  const trialHasEnded = trialEndDate ? new Date() > trialEndDate : false;
  const notSubscribed = subscription?.status !== 'active';
  const showPayments = trialHasEnded && notSubscribed;

  return (
    <div className={styles.chatClient}>
      <div className={styles.chatMessages} ref={chatMessagesRef}>
        {messages.map((message) => {
          if (message.deleted) {
            return null;
          }

          const wasSentByMe = message.idMember === me?.id;

          let member;

          if (message.idMember === 'bot') {
            member = {
              fullName: 'Tiny Power-Ups Club',
              avatar: 'https://powerups.club/images/tinypups-auth-icon.png',
            };
          } else {
            member = members.get(message.idMember);
          }

          const messageElement = (
            <div className={styles.messageContainer}>
              <div className={styles.messageHeader}>
                <Avatar appearance="circle" src={member?.avatar} size="small" />
                <span className={styles.messageSenderName}>
                  {member?.fullName ?? 'Unknown User'}
                </span>
                <span className={styles.timestamp}>
                  {message.createdAt > new Date()
                    ? 'now'
                    : moment(message.createdAt).fromNow()}
                </span>
              </div>
              <div className={styles.messageText}>{message.text}</div>
              <div className={styles.messageActions}>
                {wasSentByMe && (
                  <LoadingButton
                    onClick={(e) => {
                      trelloIframe.popup({
                        mouseEvent: e,
                        type: 'confirm',
                        title: `Delete this message`,
                        message: `Are you sure you would like to delete this message? There is no undo.`,
                        confirmText: 'Delete',
                        onConfirm: async function (c: any) {
                          await deleteMessage(message.id);

                          c.set(
                            'board',
                            'shared',
                            'rerender',
                            new Date().toISOString()
                          );

                          c.closePopup();
                        },
                        confirmStyle: 'danger',
                        cancelText: 'Cancel',
                        onCancel: function (c: any) {
                          c.closePopup();
                        },
                      });
                    }}
                    iconBefore={<TrashIcon label="Delete this message" />}
                    appearance="default"
                  />
                )}
              </div>
            </div>
          );

          return (
            <React.Fragment key={message.id}>{messageElement}</React.Fragment>
          );
        })}
      </div>
      <div className={styles.inputContainer}>
        <TextField
          autoFocus
          placeholder={
            isAuthorized
              ? showPayments
                ? 'Your free trial is up. Join the Power-Ups Club to keep chatting.'
                : 'Type your message here...'
              : 'You must authorize the Board Chat Power-Up with Trello to participate in this chat.'
          }
          isDisabled={!isAuthorized || showPayments}
          value={incomingMessage}
          onChange={(e) => setIncomingMessage(e.currentTarget.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              onSubmit();
            }
          }}
        />
        {isAuthorized ? (
          <LoadingButton
            isLoading={isSending}
            appearance="primary"
            onClick={showPayments ? onPayments : onSubmit}
          >
            {showPayments ? 'Join the Power-Ups Club!' : 'Send'}
          </LoadingButton>
        ) : (
          <LoadingButton
            onClick={authorize}
            isLoading={isAuthorizing}
            appearance="primary"
          >
            Authorize Board Chat
          </LoadingButton>
        )}
      </div>
    </div>
  );
};
